import moment from "moment";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import HandBookTemplateApprovingCell from "pages/HandBook/features/HandBookTemplateApprovingCell/HandBookTemplateApprovingCell";
import { HANDBOOK_TEMPLATE_LVL } from "pages/HandBook/model/slice";
import { getHandbookTemplates, getHandbookTemplatesMore } from "pages/HandBook/model/thunks";
import { IHandBookTemplate, IHandbookTemplatesListFilter } from "pages/HandBook/model/types";
import { handbooksModifyWithEditings } from "pages/HandBook/utils/handbooksModifyWithEditings";
import { CreateHandbookTemplate } from "pages/HandBook/widgets/CreateHandbook/CreateHandbookTemplate";
import HandBookApplyToObject from "pages/HandBook/widgets/HandBookApplyToObject/HandBookApplyToObject";

import HandBooksTemplatesRow from "../../HandBooksTemplatesRow";
import icon from "./../../../../assets/handbook.svg";
import { useTypedSelector } from "app/store/typedUseSelector";
import TableReusableHead, { TableReusableHeaderCell } from "shared/ui/dataDisplay/TableReusable/TableReusableHead";
import ListWrapper from "shared/ui/layout/ListWrapper/ListWrapper";
import RowGrid from "shared/ui/layout/RowGrid/RowGrid";

import { VIEW_HANDBOOK_APPROVALS } from "constants/permissions/generalPermissions";

import usePermission from "hooks/usePermission";

import { fullNameToShortName } from "utils/formatters/getShortFullName";
import { generateStorageKey } from "utils/helpers/generateStorageKey";
import CreateHandbookTemplateBtn from "pages/HandBook/widgets/CreateHandbook/CreateHandbookTemplateBtn";

const HandbookTemplates = () => {
  const hasHandbookApprovalPermission = usePermission(VIEW_HANDBOOK_APPROVALS);
  const history = useHistory();
  const [filter, setFilter] = useState<IHandbookTemplatesListFilter>({});
  const hasListFilters = Object.keys(filter).length > 0;
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey(restFilter as any);
  const moreKey = generateStorageKey({ ...restFilter, type: "moreTemplates" } as any);
  const dispatch = useDispatch();
  const cacheKey = useTypedSelector((state) => state.handBookReducer.invalidationKey);
  const editings = useTypedSelector((state) => state.handBookReducer.templateEditings)[HANDBOOK_TEMPLATE_LVL.TEMPLATES];
  const templates = useTypedSelector((state) => state.handBookReducer.templates)[key];
  const isLoading = useTypedSelector((state) => state.handBookReducer.loadings)[key];
  const isLoadingMore = useTypedSelector((state) => state.handBookReducer.loadings)[moreKey];

  const displayTemplates = useMemo(() => {
    if (!templates?.results) return templates?.results;
    return handbooksModifyWithEditings(templates.results, editings);
  }, [templates?.results, editings]);

  useEffect(() => {
    dispatch(getHandbookTemplates({ ...filter, limit: Math.max(templates?.results?.length ?? 10, 10) + 1 }));
  }, [cacheKey, filter]);

  const formColPercents = [3, 97];
  const colPercents = hasHandbookApprovalPermission ? [63, 2, 12, 10, 10] : [75, 2, 10, 10];
  const baseCols: ICommonColumn<IHandBookTemplate>[] = [
    { header: { title: "Шаблон", filterName: "name" }, render: (item) => item.name },
    {
      header: { title: "" },
      render: (item) => <HandBookApplyToObject template={item} />,
      isCentered: true,
    },
    {
      header: {
        title: "Дата",
        filterName: "created_at",
        filterType: "datesRange",
        rangeStartField: "created_at_after",
        rangeEndField: "created_at_before",
      },
      render: (item) => moment(item.created_at).format("DD.MM.YYYY"),
      isCentered: true,
    },
    {
      header: { title: "Автор", filterName: "author" },
      render: (item) => fullNameToShortName(item.author?.full_name || ""),
      isNoBreak: true,
    },
  ];

  const columns = hasHandbookApprovalPermission
    ? baseCols.concat([
        {
          header: { title: "Согласование" },
          render: (item) => <HandBookTemplateApprovingCell templateId={item.id} approvals={item.approval_steps} />,
          isCentered: true,
        },
      ])
    : baseCols;

  return (
    <ListWrapper
      data={displayTemplates}
      dataMapper={(item: IHandBookTemplate) => (
        <HandBooksTemplatesRow
          item={item}
          key={item.id}
          colPercents={colPercents}
          formColPercents={formColPercents}
          columns={columns}
          nameFieldPlaceholder="Введите название шаблона"
          onRowClick={() => history.push(`./templates/${item.id}`)}
        />
      )}
      header={
        <TableReusableHead isSticky isNoDefaultGridCols>
          <RowGrid percents={colPercents}>
            {columns.map((el) => (
              <TableReusableHeaderCell
                key={el.header.title}
                isCentered={el.isCentered}
                filterType={el.header.filterType ? el.header.filterType : el.header.filterName ? "text" : undefined}
                filterField={el.header.filterName}
                onChangeFilter={(name, value) => setFilter((prev) => ({ ...prev, [name]: value }))}
                filterTitle={el.header.title}
                isLast={el.isLast}
                filterOptions={el.header.filterOptions}
                isRighted={el.isRighted}
                rangeStartField={el.header.rangeStartField}
                rangeEndField={el.header.rangeEndField}
                isDebouncedFilterApply
              >
                {el.header.title}
              </TableReusableHeaderCell>
            ))}
          </RowGrid>
        </TableReusableHead>
      }
      placeholderProps={{ img: icon, text: hasListFilters ? "Нет сведений" : "Добавьте шаблон", fullHeight: false }}
      total={templates?.count}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      filter={filter}
      onPagination={() => {
        dispatch(getHandbookTemplatesMore({ ...filter, offset: templates?.results?.length }));
      }}
      postfix={
        !isLoading && (
          <>
            <CreateHandbookTemplate />
            <CreateHandbookTemplateBtn text="Добавить шаблон" />
          </>
        )
      }
    />
  );
};

export default HandbookTemplates;

//TODO доработать под нормальное реиспользование и перенести в shared
export interface ICommonColumn<T extends Record<string, any>> {
  header: {
    title: string;
    filterName?: string;
    filterType?: "select" | "datesRange";
    filterOptions?: any[];
    rangeStartField?: string;
    rangeEndField?: string;
  };
  isCentered?: boolean;
  isNoBreak?: boolean;
  isLast?: boolean;
  isRighted?: boolean;
  render: (item: T) => ReactNode;
}
